// ABOUTME: The start readers on screen and every read of their starts: the first one, and again when a change touches
// ABOUTME: days a reader covers — one at a time, so no read rides on an older one. Module state, like the chunk registry.

import type { CalendarRange } from "@/model/calendar/cache/calendarRange"
import { runSerial } from "@/model/calendar/cache/runSerial"

export interface StartsReader {
  /** Whether a change to these days of this user can move a start the reader shows. */
  covers: (range: CalendarRange) => boolean
  /** Reads the starts from the network; the reader's own query never does (cache-only). */
  refetch: () => Promise<unknown>
  /**
   * Told at once when a read of it is asked for, which may wait its turn in line behind another's: its query shows no
   * load until the read starts, and what the reader shows may be about to change.
   */
  onQueued?: () => void
}

/** Every reader on screen; the realtime provider lives above the pages and cannot reach their hooks. */
const readers: StartsReader[] = []

const STARTS_READ_KEY = "starts-read"

type Pending = ReadonlySet<StartsReader>

const mergePending = (queued: Pending, incoming: Pending): Pending => new Set([...queued, ...incoming])

/**
 * A reader that left the screen while its read waited in line is not read. Every read of the run settles before the
 * line moves on — a failure released early would let the next read ride on one still out — and a failure is then
 * runSerial's to log.
 */
async function readPending(pending: Pending): Promise<void> {
  const outcomes = await Promise.allSettled(
    readers.filter((reader) => pending.has(reader)).map((reader) => reader.refetch()),
  )
  const failure = outcomes.find((outcome): outcome is PromiseRejectedResult => outcome.status === "rejected")
  if (failure) throw failure.reason
}

/**
 * One read at a time, and never handed over: a refetch rides a disposable request (ObservableQuery.js:394-395,
 * 930-937), and Apollo hands an identical request still in flight to whoever asks again (`queryDeduplication`, on by
 * default — ApolloClient.js:76, QueryManager.js:556-571). A read sent while an older one is out would get the older
 * answer, and a race moved meanwhile would keep its old day. Queued reads merge into one. Never rejects: runSerial
 * logs a failed read and settles.
 */
function read(pending: Pending): Promise<void> {
  if (pending.size === 0) return Promise.resolve()
  return runSerial(STARTS_READ_KEY, pending, readPending, {
    merge: mergePending,
    stallMs: Number.POSITIVE_INFINITY,
  }).then(() => undefined)
}

/** Asks for a read of these readers, telling each first: it joins the line, and may wait there a while. */
function readSoon(touched: readonly StartsReader[]): Promise<void> {
  touched.forEach((reader) => reader.onQueued?.())
  return read(new Set(touched))
}

/** The reader's first read joins the line too: a change that arrives while it is out must not ride on it. */
export function registerStartsReader(reader: StartsReader): () => void {
  readers.push(reader)
  void readSoon([reader])
  return () => {
    const at = readers.lastIndexOf(reader)
    if (at !== -1) readers.splice(at, 1)
  }
}

/**
 * `ranges` null means a read of everything — a channel that could not replay what it missed, the user's own change,
 * the safety-net poll.
 */
export function refreshStartsIfTouched(ranges: readonly CalendarRange[] | null): Promise<void> {
  return readSoon(readers.filter((reader) => ranges === null || ranges.some((range) => reader.covers(range))))
}
